import * as React from 'react';

import {
  Banner,
  type BannerType, Icon, ListItem, space,
  SpaceBetween,
  Typography,
} from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import ServiceBox, { Description } from '../ServiceBox';
import { useService } from '@/scopes/checkout/domain/hooks';
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types';
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable';
import { Texts } from '@/scopes/checkout/components/services/ServiceComponents';
import { CarsListView } from '@/scopes/checkout/components/services/CarRentalFilteringService/CarsListView';
import { ExtraCarsView } from '@/scopes/checkout/components/services/CarRentalFilteringService/ExtraCarsView';
import { UpgradeCarsListView } from '@/scopes/checkout/components/services/CarRentalFilteringService/UpgradeCarsListView';
import {
  carIsBeingSelected,
  carIsBeingUpgraded, carToBeSelected,
  useCarRentalServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store';
import { CarOption } from '@/scopes/checkout/components/services/CarRentalFilteringService/CarOption';
import { DriverSheet } from '@/scopes/checkout/components/services/CarRentalFilteringService/FormDriverSheet/DriverSheet';

export function CarRentalFilteringService() {
  const intl = useIntl();
  const { service } = useService(ServiceType.CarRentalFiltering);
  useSendAncillaryAvailable(service, 'selectCarRental')
  const isCarBeingUpgraded = useCarRentalServiceStore(carIsBeingUpgraded);
  const isCarBeingSelected = useCarRentalServiceStore(carIsBeingSelected);
  const selectingCar = useCarRentalServiceStore(carToBeSelected);

  if (!service) return null;
  const { usps, carLists, options } = service;
  const selectingCarOption = selectingCar ? options?.[selectingCar] : null;

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_description' })}
      glyph="IconCar"
      modalTitle=""
      modalDescription=""
    >
      <Description>
        <Typography>
          <ul>
            {usps?.map(({ link, text }) => (
              <li key={text}>
                {link && <a href={link}>{text}</a>}
                {!link && text}
              </li>
            ))}
          </ul>
        </Typography>
      </Description>

      <WarningMessages />

      {/*WHEN CAR IS ALREADY SELECTED AND DRIVER*/}
      {carLists && carLists?.length > 0 && (
        <SpaceBetween direction='y' spacing={space('primitives_space_16')}>
          { carLists?.map(({ selectedId, selectedValue }: { selectedId?: string, selectedValue: string }, index) => {
            const optionId = selectedId || selectedValue;
            const selectedOption = options?.[optionId];
            if (!selectedOption) return null;
            return (
              <SpaceBetween direction='y' spacing={space('primitives_space_8')} key={optionId}>
                { service.isOpenJawPackage && selectedOption.openJawFeeAmount > 0 && <OpenJawMessage openJawFeeAmount={selectedOption.openJawFeeAmount} /> }
                <CarOption isSelected {...selectedOption}  />
                <CarOptionForms {...selectedOption} />
                {service.maxNumbersOfCarsToBeBooked > carLists?.length && (carLists?.length === index + 1) && <ExtraCarsView {...service} />}
              </SpaceBetween>
            );
        })}
        </SpaceBetween>
      )}

      {/*First selection made*/}
      { isCarBeingSelected && selectingCarOption && (
        <SpaceBetween direction='y' spacing={space('primitives_space_8')}>
          <CarOption isSelected {...selectingCarOption}  />
          <CarOptionForms {...selectingCarOption} openDriverByDefault />
        </SpaceBetween>
      )}

      {/*Initial state*/}
      {!selectingCarOption && carLists && carLists?.length === 0 && <CarsListView />}

      <UpgradeCarsListView
        open={isCarBeingUpgraded}
      />
     </ServiceBox>
  );
}

function WarningMessages() {
  const intl = useIntl();
  const { service } = useService(ServiceType.CarRentalFiltering);

  if (!service) return null;

  return (
    <SpaceBetween direction="y" spacing={space('primitives_space_8')}>
      { service.includedCarOutOfStock && <Banner $type={'warning' as BannerType}>
        <Texts direction="y">
          <Typography $variant="label">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_not_available_title' })}</Typography>
          <Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_included_filtering_car_not_available_description' })}</Typography>
        </Texts>
      </Banner>
      }
      { service.includedCarPriceChanged && <Banner $type={'warning' as BannerType}>
        <Texts direction="y">
          <Typography $variant="label">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_price_changed_title' })}</Typography>
          <Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_included_filtering_car_price_changed_description' })}</Typography>
        </Texts>
      </Banner>
      }
    </SpaceBetween>
  );
}

function OpenJawMessage({ openJawFeeAmount }: { openJawFeeAmount: number }) {
  const intl = useIntl();
  return (
    <Banner $type={'info' as BannerType}>
      <Texts direction="y">
        <Typography $variant="label"> {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_open_jaw_car_title' })}</Typography>
        <Typography>
          {intl.formatMessage(
            { id: 'checkout_steps_ancillaries_car_rental_open_jaw_car_description' },
            { openJawFeeAmount:  intl.formatCurrency(openJawFeeAmount)}
          )}
        </Typography>
      </Texts>
    </Banner>
  );
}



function CarOptionForms({openDriverByDefault = false, ...option }: CheckoutCarRentalServiceOption & { openDriverByDefault?: boolean  }) {
  return <>
    <DriverSheet {...option} openByDefault={openDriverByDefault}/>
    {/*<EquipmentsForm />*/}
  </>
}

function EquipmentsForm() {
  return <ListItem.Root>
    <ListItem.Content>
      <ListItem.Label>Equipments</ListItem.Label>
      <ListItem.Description>Equioments here (Pending to be implemented)</ListItem.Description>
    </ListItem.Content>
    <ListItem.Trailing>
      <Icon glyph="IconChevronRight"/>
    </ListItem.Trailing>
  </ListItem.Root>
}
